import React, { useEffect, useState, useRef } from "react";

// Module-level state to persist open tooltip across re-mounts
let globalTooltipData = null; // { fileId, title, remark, anchorRect }
const globalListeners = new Set();

export function setGlobalTooltip(data) {
  globalTooltipData = data;
  globalListeners.forEach((fn) => fn());
}

export function closeGlobalTooltip() {
  globalTooltipData = null;
  globalListeners.forEach((fn) => fn());
}

// Hook to check if a specific tooltip is open
export function useIsTooltipOpen(fileId) {
  const [isOpen, setIsOpen] = useState(globalTooltipData?.fileId === fileId);
  useEffect(() => {
    const listener = () => setIsOpen(globalTooltipData?.fileId === fileId);
    globalListeners.add(listener);
    listener();
    return () => globalListeners.delete(listener);
  }, [fileId]);
  return isOpen;
}

// Standalone component to render the tooltip — mount ONCE high in the tree
const GlobalTooltip = () => {
  const [data, setData] = useState(globalTooltipData);
  const tooltipElRef = useRef(null);

  useEffect(() => {
    const listener = () =>
      setData(globalTooltipData ? { ...globalTooltipData } : null);
    globalListeners.add(listener);
    listener();
    return () => globalListeners.delete(listener);
  }, []);

  useEffect(() => {
    if (!data) return;
    const handleClickOutside = (e) => {
      if (tooltipElRef.current && !tooltipElRef.current.contains(e.target)) {
        const clickedAnchor = e.target.closest("[data-tooltip-trigger]");
        if (!clickedAnchor) {
          closeGlobalTooltip();
        }
      }
    };
    const handleScroll = (e) => {
      if (tooltipElRef.current && tooltipElRef.current.contains(e.target)) {
        return;
      }
      closeGlobalTooltip();
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [data]);

  if (!data) return null;

  const { title, remark, anchorRect } = data;
  const tooltipWidth = 500;
  const tooltipMaxHeight = window.innerHeight * 0.6;
  const spaceRight = window.innerWidth - anchorRect.right;
  const spaceLeft = anchorRect.left;

  let top, left;

  // Horizontal: prefer right of anchor, then left, then align with anchor left
  if (spaceRight > tooltipWidth + 20) {
    left = anchorRect.right + 10;
  } else if (spaceLeft > tooltipWidth + 20) {
    left = anchorRect.left - tooltipWidth - 10;
  } else {
    left = Math.max(10, (window.innerWidth - tooltipWidth) / 2);
  }

  // Vertical: center vertically near anchor, clamped within viewport
  top = anchorRect.top;
  // Ensure it doesn't go below viewport
  if (top + tooltipMaxHeight > window.innerHeight - 10) {
    top = window.innerHeight - tooltipMaxHeight - 10;
  }
  // Ensure it doesn't go above viewport
  if (top < 10) {
    top = 10;
  }

  const position = { top, left };

  return (
    <div
      ref={tooltipElRef}
      style={{
        position: "fixed",
        zIndex: 99,
        padding: 0,
        color: "#333",
        borderRadius: 12,
        background: "#fff",
        boxShadow: "0px 4px 11px 3px rgba(0, 0, 0, 0.22)",
        fontFamily: "DM Sans",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "normal",
        width: tooltipWidth,
        maxHeight: "60vh",
        overflowX: "hidden",
        overflowY: "auto",
        ...position,
      }}
    >
      <div
        style={{
          padding: 10,
          width: "100%",
          border: "2px solid #00b17e",
          borderRadius: 12,
        }}
      >
        <p style={{ fontSize: 14, fontWeight: "bold", marginBottom: 4 }}>
          {title}
        </p>
        <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
          <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {remark}
          </p>
        </div>
      </div>
    </div>
  );
};

export default GlobalTooltip;
